import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'

import { TextField } from '@mxenabled/mxui'
import { ExpandMore as ExpandMoreIcon } from '@mxenabled/mx-icons'

import {
  CurrencyInput,
  SelectionBox,
  useAccountStore,
  useGlobalCopyStore,
} from '../../../../common'
import { TransactionType } from '../../../../core'

import { useTransactionsUiStore } from '../../../context/hooks'

import { CategoryAction, DateAction, MemoAction, TagsAction } from './actions'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'

type ManualAccount = {
  label: string
  value: string
}
const ManualTransactionDetails = () => {
  const { visibleAccounts: accounts } = useAccountStore()
  const { common: commonCopy, transactions: copy } = useGlobalCopyStore()
  const { manualTransaction, setIsAmountValid, updateManualTransaction } = useTransactionsUiStore()

  const [amount, setAmount] = React.useState('')
  const [payee, setPayee] = React.useState('')

  const manualAccounts = React.useMemo(
    () => accounts.filter((account) => account.is_manual),
    [accounts],
  ).map((account) => {
    return {
      label: `${account.name} | ${formatCurrency(account.balance, '0,0.00')}`,
      value: account.guid,
    } as ManualAccount
  })

  const currencyCopy = {
    error_amount_is_invalid: commonCopy.currency_input.error_amount_is_invalid,
    error_amount_must_be_equal_to: commonCopy.currency_input.error_amount_must_be_equal_to,
    error_amount_must_be_less_than_or_equal_to:
      commonCopy.currency_input.error_amount_must_be_less_than_or_equal_to,
    enter_amount: commonCopy.currency_input.enter_amount,
  }

  const handleAmountChanged = (amount: string) => {
    setAmount(amount)
    updateManualTransaction({ ...manualTransaction, amount: Number(amount) })
  }

  const handleTransactionTypeChanged = (_event: React.ChangeEvent<HTMLElement>, value: string) => {
    updateManualTransaction({ ...manualTransaction, transaction_type: Number(value) })
  }

  const handlePayeeChanged = (payee: string) => {
    setPayee(payee)
    updateManualTransaction({ ...manualTransaction, payee, description: payee })
  }

  const handleAccountChanged = (guid: string) => {
    updateManualTransaction({ ...manualTransaction, account_guid: guid })
  }

  return (
    <Box className="mx-txn-manual-transaction-details">
      <Stack gap={40} mx={24} my={32}>
        <Stack
          direction="row"
          sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 40, sm: 8 } }}
        >
          <CurrencyInput
            allowDecimals={true}
            amount={amount}
            ariaLabel={copy.amount_title}
            copy={currencyCopy}
            label={copy.amount_title}
            setAmount={handleAmountChanged}
            setHasValidInput={setIsAmountValid}
            sx={{ bgcolor: 'background.paper' }}
            type="number"
          />
          <RadioGroup name="TransactionType" onChange={handleTransactionTypeChanged} row={true}>
            <Stack alignItems="center" direction="row">
              <SelectionBox
                checked={manualTransaction.transaction_type === TransactionType.CREDIT}
                label={copy.manual_transaction_credit_button_label}
                sx={{ ml: { xs: 0, sm: 8 } }}
                tooltip={copy.manual_transaction_credit_button_tooltip}
                value={TransactionType.CREDIT}
              />
              <SelectionBox
                checked={manualTransaction.transaction_type === TransactionType.DEBIT}
                label={copy.manual_transaction_debit_button_label}
                tooltip={copy.manual_transaction_debit_button_tooltip}
                value={TransactionType.DEBIT}
              />
            </Stack>
          </RadioGroup>
        </Stack>

        <TextField
          label={copy.payee_title}
          name="payee"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePayeeChanged(e.target.value)}
          sx={{ bgcolor: 'background.paper' }}
          value={payee}
        />

        <TextField
          label={copy.manual_transaction_select_an_account}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleAccountChanged(e.target.value)
          }
          placeholder={copy.manual_transaction_select}
          select={true}
          slotProps={{
            inputLabel: { shrink: true },
            select: { defaultValue: '', IconComponent: ExpandMoreIcon },
          }}
          sx={{ bgcolor: 'background.paper' }}
        >
          {manualAccounts.map((account) => (
            <MenuItem key={account.value} value={account.value}>
              {account.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Divider light={true} />
      <Box bgcolor="background.paper">
        <CategoryAction transaction={manualTransaction} />
        <DateAction transaction={manualTransaction} />
        <MemoAction transaction={manualTransaction} />
        <TagsAction transaction={manualTransaction} />
      </Box>
    </Box>
  )
}

export default observer(ManualTransactionDetails)
